import { z } from "zod";

import { LICENCE_ACTIVITY_TYPES } from "../constants/licence-activity-types.js";

/** One of `LICENCE_ACTIVITY_TYPES` (#566). */
export const LicenceActivityTypeSchema = z.enum(LICENCE_ACTIVITY_TYPES);

/** The default and the ceiling of `GET /admin/licences/:id/activity?limit=`. */
export const LICENCE_ACTIVITY_DEFAULT_LIMIT = 20;
export const LICENCE_ACTIVITY_MAX_LIMIT = 100;

/**
 * What an event carries to be displayed without a second query, captured when it
 * was written, so the feed still reads right after the list is renamed or the
 * user deleted. One flat object for every type rather than a union, so Fastify's
 * serializer keeps every field: the `user_*` trio is set on the three user events
 * (`user_added`, `user_invited`, `client_logged_in`), `list_name` on the three list
 * events, `approver_name` on `list_approval_requested`. Absent otherwise.
 */
export const LicenceActivityMetadataSchema = z.object({
    user_first_name: z.string().optional(),
    user_last_name: z.string().optional(),
    user_email: z.string().optional(),
    list_name: z.string().optional(),
    /** `list_approval_requested` (#577): who was asked to approve. */
    approver_name: z.string().optional(),
});
export type LicenceActivityMetadata = z.infer<typeof LicenceActivityMetadataSchema>;

/** Who did it, as they are now. Null when unknown (a back-filled event) or deleted. */
export const LicenceActivityActorSchema = z.object({
    id: z.uuid(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.string(),
});
export type LicenceActivityActor = z.infer<typeof LicenceActivityActorSchema>;

export const LicenceActivityEventSchema = z.object({
    id: z.uuid(),
    type: LicenceActivityTypeSchema,
    actor: LicenceActivityActorSchema.nullable(),
    /** `user` or `list`: what the event is about. */
    ref_type: z.string().nullable(),
    ref_id: z.string().nullable(),
    metadata: LicenceActivityMetadataSchema,
    created_at: z.string(),
});
export type LicenceActivityEvent = z.infer<typeof LicenceActivityEventSchema>;

/**
 * One page of the feed, newest first. `next_cursor` is opaque: pass it back as
 * `?cursor=` for the next (older) page; null when this page is the last.
 */
export const LicenceActivityPageSchema = z.object({
    items: z.array(LicenceActivityEventSchema),
    next_cursor: z.string().nullable(),
});
export type LicenceActivityPage = z.infer<typeof LicenceActivityPageSchema>;

export const LicenceActivityPageResponseSchema = z.object({
    success: z.boolean(),
    data: LicenceActivityPageSchema,
});
export type LicenceActivityPageResponse = z.infer<typeof LicenceActivityPageResponseSchema>;
